import React, { useState, useEffect } from 'react';
import { Clock, ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useEditorStore } from '../lib/editorStore';

interface Version {
  id: string;
  version: number;
  created_at: string;
  created_by: string;
}

export function VersionHistory() {
  const [versions, setVersions] = useState<Version[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const workflowId = useEditorStore((state) => state.workflowId);
  const currentVersion = useEditorStore((state) => state.version);

  useEffect(() => {
    if (workflowId) {
      loadVersions();
    }
  }, [workflowId]);

  const loadVersions = async () => {
    if (!workflowId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('workflow_versions')
        .select('*')
        .eq('workflow_id', workflowId)
        .order('version', { ascending: false });

      if (error) throw error;
      setVersions(data || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Error loading versions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const restoreVersion = async (versionId: string) => {
    if (!workflowId) return;
    
    try {
      const { data, error } = await supabase
        .from('workflow_versions')
        .select('flow_data')
        .eq('id', versionId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Version not found');

      const flowData = data.flow_data;
      useEditorStore.setState({
        nodes: flowData.nodes,
        edges: flowData.edges
      });

      // Save as new version
      await useEditorStore.getState().saveWorkflow();
    } catch (err: any) {
      console.error('Error restoring version:', err);
      setError(err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-400" />
          Version History
        </h3>
        <button
          onClick={loadVersions}
          className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-gray-300"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {versions.map((version) => (
          <div
            key={version.id}
            className={`flex items-center justify-between p-3 rounded-lg ${
              version.version === currentVersion
                ? 'bg-indigo-500/10 border border-indigo-500/50'
                : 'bg-gray-700/50 hover:bg-gray-700'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium text-gray-300">
                v{version.version}
              </div>
              <div className="text-xs text-gray-400">
                {new Date(version.created_at).toLocaleString()}
              </div>
            </div>
            {version.version !== currentVersion && (
              <button
                onClick={() => restoreVersion(version.id)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 rounded"
              >
                <RotateCcw className="w-3 h-3" />
                Restore
              </button>
            )}
          </div>
        ))}
      </div>

      {versions.length === 0 && (
        <div className="text-center py-4 text-gray-400">
          No versions available
        </div>
      )}
    </div>
  );
}